import { DocumentMappingsResponse } from "@dune/types";
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { ClipboardCopy } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import PlaceholdersTabSkeleton from "../skeletons/PlaceholdersTabSkeleton";
import StaticTagsSection from "./StaticTagsSection";

const ENTITY_DISPLAY_NAMES: Record<string, string> = {
	society: "Apporteurs",
	constructionSite: "Chantier",
	gFAConstructionSite: "Chantier GFA",
	proposition: "Proposition",
	user: "Utilisateur",
	propositionDocument: "propositionDocument",
	receipt: "Quittance",
	address: "Adresse",
	participant: "Intervenant",
	participantToConstructionSiteSubmission: "Intervenant à la soumission",
	foreignSociety: "Société étrangère",
};

/**
 * Retourne le nom d'affichage français pour un modèle Prisma
 */
function getEntityDisplayName(prismaModel: string): string {
	return ENTITY_DISPLAY_NAMES[prismaModel] || prismaModel;
}

/**
 * Find the entity key for a given model name by checking prismaModel values
 * @param modelName - The model name from a relation (e.g., "users", "user", "data")
 * @param documentMappings - The document mappings object
 * @returns The entity key if found, null otherwise
 */
function findEntityKeyByModelName(
	modelName: string,
	documentMappings: DocumentMappingsResponse["documentMappings"],
): string | null {
	for (const [entityKey, mapping] of Object.entries(documentMappings)) {
		if (mapping.prismaModel === modelName) {
			return entityKey;
		}
	}

	const singularForm = modelName.endsWith("s")
		? modelName.slice(0, -1)
		: modelName;
	for (const [entityKey, mapping] of Object.entries(documentMappings)) {
		if (mapping.prismaModel === singularForm) {
			return entityKey;
		}
	}

	return null;
}

interface PlaceholdersTabContentProps {
	isLoading: boolean;
	error?: Error | null;
	mappings?: DocumentMappingsResponse;
	handleCopy: (model: string, field: string) => void;
}

const PlaceholdersTabContent: React.FC<PlaceholdersTabContentProps> = ({
	isLoading,
	error,
	mappings,
	handleCopy,
}) => {
	if (isLoading) {
		return <PlaceholdersTabSkeleton />;
	}

	if (error) {
		return <p className="text-red-500">Erreur : {error.message}</p>;
	}

	if (!mappings) {
		return <p className="text-gray-500">Aucune donnée disponible</p>;
	}

	const handleStaticTagCopy = (tag: string) => {
		navigator.clipboard.writeText(tag);
		toast.success(`Copié : ${tag}`);
	};

	const handleParameterizedTagCopy = (key: string) => {
		const tag = `{{${key}}}`;
		navigator.clipboard.writeText(tag);
		toast.success(`Copié : ${tag}`);
	};

	return (
		<div className="space-y-6">
			<StaticTagsSection
				staticTags={mappings.staticTags}
				onCopy={handleStaticTagCopy}
			/>

			{mappings.parameterizedTags &&
				Object.keys(mappings.parameterizedTags).length > 0 && (
					<Card className="mb-6">
						<CardHeader>
							<CardTitle className="text-lg font-semibold">
								Tags avec paramètres
							</CardTitle>
							<p className="text-sm text-gray-600">
								Ces balises nécessitent une sélection manuelle lors de la
								génération du document.
							</p>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Balise</TableHead>
										<TableHead>Libellé</TableHead>
										<TableHead>Description</TableHead>
										<TableHead className="w-16">Action</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{Object.entries(mappings.parameterizedTags).map(
										([key, tag]) => (
											<TableRow key={`parameterized-tag-${key}`}>
												<TableCell className="font-mono text-sm max-w-xs truncate">
													{key}
												</TableCell>
												<TableCell>{tag.label}</TableCell>
												<TableCell className="text-sm text-gray-600">
													{tag.description}
												</TableCell>
												<TableCell>
													<Button
														variant="ghost"
														size="icon"
														onClick={() => handleParameterizedTagCopy(key)}
														title="Copier la balise paramétrable"
													>
														<ClipboardCopy className="h-4 w-4" />
													</Button>
												</TableCell>
											</TableRow>
										),
									)}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				)}

			<Card className="relative">
				<CardHeader>
					<CardTitle className="text-lg font-semibold">
						Champs d'entités
					</CardTitle>
					<p className="text-sm text-gray-600">
						Champs disponibles pour chaque type d'entité du système.
					</p>
				</CardHeader>

				<div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
					<div className="p-4 bg-gray-50 rounded-lg">
						<h4 className="text-sm font-medium text-gray-900 mb-3">
							Navigation rapide :
						</h4>

						<div className="flex flex-wrap gap-2">
							{Object.entries(mappings.documentMappings)
								.filter(([, mapping]) => !mapping.hidden)
								.map(([key, mapping]) => {
									// Calculate relation fields count
									let relationFieldsCount = 0;
									if (mappings.relations?.[key]) {
										for (const [_, relationConfig] of Object.entries(
											mappings.relations[key],
										)) {
											const relatedEntityKey = findEntityKeyByModelName(
												relationConfig.model,
												mappings.documentMappings,
											);
											if (relatedEntityKey) {
												const relatedMapping =
													mappings.documentMappings[relatedEntityKey];
												relationFieldsCount +=
													relatedMapping?.fields?.length || 0;
											}
										}
									}

									return (
										<Button
											key={`entity-nav-button-${key}`}
											variant="outline"
											size="sm"
											onClick={() => {
												const element = document.getElementById(
													`model-${mapping.prismaModel}`,
												);
												element?.scrollIntoView({
													behavior: "smooth",
													block: "start",
												});
											}}
											className="text-xs"
										>
											{getEntityDisplayName(mapping.prismaModel)}
											<span className="ml-1 text-gray-600">
												(
												{mapping.fields.length +
													(mapping.computedFields?.length || 0) +
													relationFieldsCount}
												)
											</span>
										</Button>
									);
								})}
						</div>
					</div>
				</div>

				<CardContent className="space-y-8 overflow-y-auto max-h-[600px]">
					{Object.entries(mappings.documentMappings)
						.filter(([, mapping]) => !mapping.hidden)
						.map(([key, mapping]) => (
							<div
								key={`model-section-${key}`}
								id={`model-${mapping.prismaModel}`}
								className="scroll-mt-24"
							>
								<div className="mb-4 pb-2 border-b border-gray-200">
									<h3 className="text-lg font-semibold text-gray-900 capitalize">
										{getEntityDisplayName(mapping.prismaModel)}
									</h3>
									<p className="text-sm text-gray-600">
										{mapping.fields.length} champ
										{mapping.fields.length > 1 ? "s" : ""}
										{mapping.computedFields &&
											mapping.computedFields.length > 0 &&
											` + ${mapping.computedFields.length} champ${mapping.computedFields.length > 1 ? "s" : ""} calculé${mapping.computedFields.length > 1 ? "s" : ""}`}
										{(() => {
											let relationFieldsCount = 0;
											let tableFieldsCount = 0;

											if (mappings.relations?.[key]) {
												for (const [_, relationConfig] of Object.entries(
													mappings.relations[key],
												)) {
													const relatedEntityKey = findEntityKeyByModelName(
														relationConfig.model,
														mappings.documentMappings,
													);
													if (relatedEntityKey) {
														const relatedMapping =
															mappings.documentMappings[relatedEntityKey];
														relationFieldsCount +=
															relatedMapping?.fields?.length || 0;
													}
												}
											}

											if (mappings.tableDataSources?.[key]) {
												tableFieldsCount = Object.keys(
													mappings.tableDataSources[key],
												).length;
											}

											let result = "";
											if (relationFieldsCount > 0) {
												result += ` + ${relationFieldsCount} champ${relationFieldsCount > 1 ? "s" : ""} de relation`;
											}
											if (tableFieldsCount > 0) {
												result += ` + ${tableFieldsCount} tableau${tableFieldsCount > 1 ? "x" : ""}`;
											}
											return result;
										})()}
									</p>
								</div>

								<div className="overflow-x-auto">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead className="min-w-[200px]">Champ</TableHead>
												<TableHead>Libellé</TableHead>
												<TableHead>Type</TableHead>
												<TableHead className="w-16">Action</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{/* Champs normaux */}
											{mapping.fields.map((field: string) => (
												<TableRow key={`normal-field-${key}-${field}`}>
													<TableCell className="font-mono text-sm max-w-xs truncate">
														{field}
													</TableCell>
													<TableCell>{mapping.labels?.[field] ?? ""}</TableCell>
													<TableCell>
														<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
															Standard
														</span>
													</TableCell>
													<TableCell>
														<Button
															variant="ghost"
															size="icon"
															onClick={() =>
																handleCopy(mapping.prismaModel, field)
															}
															title="Copier le champ"
														>
															<ClipboardCopy className="h-4 w-4" />
														</Button>
													</TableCell>
												</TableRow>
											))}

											{(mapping.computedFields || []).map((field: string) => (
												<TableRow
													key={`computed-field-${key}-${field}`}
													className="bg-blue-50"
												>
													<TableCell className="font-mono text-sm font-semibold text-blue-700 max-w-xs truncate">
														{field}
													</TableCell>
													<TableCell>{mapping.labels?.[field] ?? ""}</TableCell>
													<TableCell>
														<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
															Calculé
														</span>
													</TableCell>
													<TableCell>
														<Button
															variant="ghost"
															size="icon"
															onClick={() =>
																handleCopy(mapping.prismaModel, field)
															}
															title="Copier le champ calculé"
														>
															<ClipboardCopy className="h-4 w-4" />
														</Button>
													</TableCell>
												</TableRow>
											))}
											{/* Relations avec sélection personnalisée */}
											{mapping.dataSourceFields &&
												Object.entries(mapping.dataSourceFields).flatMap(
													([relationName, customFields]) => {
														return customFields.fields.map((field: string) => {
															const fullPath = `${relationName}.${field}`;
															const fieldLabel =
																customFields.labels?.[field] ?? field;
															return (
																<TableRow
																	key={`custom-relation-field-${key}-${fullPath}`}
																	className="bg-purple-50"
																>
																	<TableCell className="font-mono text-sm font-semibold text-purple-700 max-w-xs truncate">
																		{fullPath}
																	</TableCell>
																	<TableCell>{fieldLabel}</TableCell>
																	<TableCell>
																		<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
																			Relation (sélection personnalisée)
																		</span>
																	</TableCell>
																	<TableCell>
																		<Button
																			variant="ghost"
																			size="icon"
																			onClick={() =>
																				handleCopy(
																					mapping.prismaModel,
																					fullPath,
																				)
																			}
																			title="Copier la relation"
																		>
																			<ClipboardCopy className="h-4 w-4" />
																		</Button>
																	</TableCell>
																</TableRow>
															);
														});
													},
												)}
											{/* Relations de cette entité */}
											{mappings.relations?.[key] &&
												Object.entries(mappings.relations[key]).flatMap(
													([relationName, relationConfig]) => {
														// Otherwise, recursively collect all fields from this relation
														const collectRelationFields = (
															relModel: string,
															prefix: string,
															relLabel: string,
															depth = 0,
														): Array<{
															path: string;
															label: string;
															isNested: boolean;
															isComputed?: boolean;
														}> => {
															if (depth > 3) return []; // Prevent infinite recursion

															const entityKey = findEntityKeyByModelName(
																relModel,
																mappings.documentMappings,
															);
															if (!entityKey) return [];

															const entityMapping =
																mappings.documentMappings[entityKey];
															if (!entityMapping) return [];

															const result: Array<{
																path: string;
																label: string;
																isNested: boolean;
																isComputed?: boolean;
															}> = [];

															// Add direct fields
															for (const field of entityMapping.fields || []) {
																result.push({
																	path: `${prefix}.${field}`,
																	label: `${relLabel} - ${entityMapping.labels?.[field] || field}`,
																	isNested: depth > 0,
																	isComputed: false,
																});
															}

															// Add computed fields
															for (const field of entityMapping.computedFields ||
																[]) {
																result.push({
																	path: `${prefix}.${field}`,
																	label: `${relLabel} - ${entityMapping.labels?.[field] || field}`,
																	isNested: depth > 0,
																	isComputed: true,
																});
															}

															// Recursively add nested relation fields
															if (mappings.relations?.[entityKey]) {
																for (const [
																	nestedRelName,
																	nestedRelConfig,
																] of Object.entries(
																	mappings.relations[entityKey],
																)) {
																	const nestedFields = collectRelationFields(
																		nestedRelConfig.model,
																		`${prefix}.${nestedRelName}`,
																		`${relLabel} > ${nestedRelConfig.label}`,
																		depth + 1,
																	);
																	result.push(...nestedFields);
																}
															}

															return result;
														};

														const allFields = collectRelationFields(
															relationConfig.model,
															relationName,
															relationConfig.label || relationName,
														);

														return allFields.map(
															({ path, label, isNested, isComputed }) => {
																const bgClass = isComputed
																	? isNested
																		? "bg-blue-50/50"
																		: "bg-blue-50"
																	: isNested
																		? "bg-purple-50/50"
																		: "bg-purple-50";

																const textClass = isComputed
																	? isNested
																		? "pl-8 text-blue-600"
																		: "font-semibold text-blue-700"
																	: isNested
																		? "pl-8 text-purple-600"
																		: "font-semibold text-purple-700";

																const badgeClass = isComputed
																	? "bg-blue-100 text-blue-800"
																	: "bg-purple-100 text-purple-800";

																const badgeText = isComputed
																	? isNested
																		? "Calculé (imbriqué)"
																		: "Calculé (relation)"
																	: isNested
																		? "Relation imbriquée"
																		: "Relation";

																return (
																	<TableRow
																		key={`relation-field-${key}-${path}`}
																		className={bgClass}
																	>
																		<TableCell
																			className={`font-mono text-sm ${textClass} max-w-xs truncate`}
																		>
																			{path}
																		</TableCell>
																		<TableCell>{label}</TableCell>
																		<TableCell>
																			<span
																				className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}
																			>
																				{badgeText}
																			</span>
																		</TableCell>
																		<TableCell>
																			<Button
																				variant="ghost"
																				size="icon"
																				onClick={() =>
																					handleCopy(mapping.prismaModel, path)
																				}
																				title="Copier la relation"
																			>
																				<ClipboardCopy className="h-4 w-4" />
																			</Button>
																		</TableCell>
																	</TableRow>
																);
															},
														);
													},
												)}
											{/* Tableaux de données */}
											{mappings.tableDataSources?.[key] &&
												Object.entries(mappings.tableDataSources[key]).flatMap(
													([tableName, tableConfig]) => {
														// Add the table marker itself
														const tableRows = [
															<TableRow
																key={`table-marker-${key}-${tableName}`}
																className="bg-orange-50"
															>
																<TableCell className="font-mono text-sm font-semibold text-orange-700 max-w-xs truncate">
																	{tableName}
																</TableCell>
																<TableCell>{tableConfig.label}</TableCell>
																<TableCell>
																	<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
																		Tableau
																	</span>
																</TableCell>
																<TableCell>
																	<Button
																		variant="ghost"
																		size="icon"
																		onClick={() =>
																			handleCopy(mapping.prismaModel, tableName)
																		}
																		title="Copier le marqueur de tableau"
																	>
																		<ClipboardCopy className="h-4 w-4" />
																	</Button>
																</TableCell>
															</TableRow>,
														];

														// Add fields for tables with related entities
														if (tableConfig.model !== "string") {
															const relatedEntityKey = findEntityKeyByModelName(
																tableConfig.model,
																mappings.documentMappings,
															);
															if (relatedEntityKey) {
																const relatedMapping =
																	mappings.documentMappings[relatedEntityKey];
																const tableFields =
																	relatedMapping?.fields || [];

																for (const field of tableFields) {
																	tableRows.push(
																		<TableRow
																			key={`table-field-${key}-${tableName}-${field}`}
																			className="bg-orange-50/50"
																		>
																			<TableCell className="font-mono text-sm text-orange-600 pl-8 max-w-xs truncate">
																				{tableName}.{field}
																			</TableCell>
																			<TableCell>
																				{tableConfig.label} -{" "}
																				{relatedMapping?.labels?.[field] ||
																					field}
																			</TableCell>
																			<TableCell>
																				<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
																					Champ de tableau
																				</span>
																			</TableCell>
																			<TableCell>
																				<Button
																					variant="ghost"
																					size="icon"
																					onClick={() =>
																						handleCopy(
																							mapping.prismaModel,
																							`${tableName}.${field}`,
																						)
																					}
																					title="Copier le champ de tableau"
																				>
																					<ClipboardCopy className="h-4 w-4" />
																				</Button>
																			</TableCell>
																		</TableRow>,
																	);
																}

																if (tableConfig.computedFields) {
																	for (const [
																		computedFieldName,
																		computedFieldConfig,
																	] of Object.entries(
																		tableConfig.computedFields,
																	)) {
																		tableRows.push(
																			<TableRow
																				key={`table-computed-field-${key}-${tableName}-${computedFieldName}`}
																				className="bg-blue-50/50"
																			>
																				<TableCell className="font-mono text-sm font-semibold text-blue-700 pl-8 max-w-xs truncate">
																					{tableName}.{computedFieldName}
																				</TableCell>
																				<TableCell>
																					{tableConfig.label} -{" "}
																					{computedFieldConfig.label}
																				</TableCell>
																				<TableCell>
																					<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
																						Champ calculé (tableau)
																					</span>
																				</TableCell>
																				<TableCell>
																					<Button
																						variant="ghost"
																						size="icon"
																						onClick={() =>
																							handleCopy(
																								mapping.prismaModel,
																								`${tableName}.${computedFieldName}`,
																							)
																						}
																						title="Copier le champ calculé du tableau"
																					>
																						<ClipboardCopy className="h-4 w-4" />
																					</Button>
																				</TableCell>
																			</TableRow>,
																		);
																	}
																}
															}
														} else {
															// For string arrays, add special fields
															tableRows.push(
																<TableRow
																	key={`table-field-${key}-${tableName}-value`}
																	className="bg-orange-50/50"
																>
																	<TableCell className="font-mono text-sm text-orange-600 pl-8 max-w-xs truncate">
																		{tableName}.value
																	</TableCell>
																	<TableCell>
																		{tableConfig.label} - Valeur
																	</TableCell>
																	<TableCell>
																		<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
																			Champ de tableau
																		</span>
																	</TableCell>
																	<TableCell>
																		<Button
																			variant="ghost"
																			size="icon"
																			onClick={() =>
																				handleCopy(
																					mapping.prismaModel,
																					`${tableName}.value`,
																				)
																			}
																			title="Copier le champ de valeur"
																		>
																			<ClipboardCopy className="h-4 w-4" />
																		</Button>
																	</TableCell>
																</TableRow>,
																<TableRow
																	key={`table-field-${key}-${tableName}-index`}
																	className="bg-orange-50/50"
																>
																	<TableCell className="font-mono text-sm text-orange-600 pl-8 max-w-xs truncate">
																		{tableName}.index
																	</TableCell>
																	<TableCell>
																		{tableConfig.label} - Index
																	</TableCell>
																	<TableCell>
																		<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
																			Champ de tableau
																		</span>
																	</TableCell>
																	<TableCell>
																		<Button
																			variant="ghost"
																			size="icon"
																			onClick={() =>
																				handleCopy(
																					mapping.prismaModel,
																					`${tableName}.index`,
																				)
																			}
																			title="Copier le champ d'index"
																		>
																			<ClipboardCopy className="h-4 w-4" />
																		</Button>
																	</TableCell>
																</TableRow>,
															);
														}

														return tableRows;
													},
												)}
										</TableBody>
									</Table>
								</div>
							</div>
						))}
				</CardContent>
			</Card>
		</div>
	);
};

export default PlaceholdersTabContent;
